/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { isEmpty } from '@ember/utils';
import actionInfo from 'cio/utils/action-info';
import { underscore } from '@ember/string';
import { ActionType } from 'cio/models/action';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import AccountService from 'cio/services/account';
import DS from 'ember-data';
import LogModel from 'cio/models/log';
import { ActivityLogType } from 'cio/utils/activity-log-types';
import { ComposerSection } from 'cio/components/action-composer';
import { CampaignType } from 'cio/models/campaign';
import { ActivityLogSource } from 'cio/utils/activity-logs';

type FormattedLogSourceActionLinkArgs = {
  log?: LogModel;
  source: ActivityLogSource;
};

export default class FormattedLogSourceActionLink extends Component<FormattedLogSourceActionLinkArgs> {
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  get isSourceWebhookTriggered() {
    return this.args.source.campaignType === CampaignType.Webhook;
  }

  get isSourceFromDifferentEnvironment() {
    if (isEmpty(this.args.source.environmentId)) {
      return false;
    }

    return Number(this.args.source.environmentId) !== Number(this.environment.current.id);
  }

  get canAccessEnvironment() {
    if (isEmpty(this.args.source.environmentId)) {
      return true;
    }

    return this.account.current.currentUser.canAccessEnvironment(String(this.args.source.environmentId));
  }

  get differentEnvironmentLinkParams() {
    let params: { route: string; models: string[] } = {
      route: 'dashboard',
      models: [String(this.args.source.environmentId)],
    };

    if (!isEmpty(this.args.source.actionId) && !isEmpty(this.args.source.campaignId)) {
      params.route = 'composer.action';
      params.models = [String(this.args.source.environmentId), String(this.args.source.actionId)];
    }

    if (isEmpty(this.args.source.actionId) && !isEmpty(this.args.source.campaignId)) {
      params.route = 'campaigns.campaign';
      params.models = [String(this.args.source.environmentId), String(this.args.source.campaignId)];
    }

    return params;
  }

  get differentEnvironmentName() {
    return this.store.peekRecord('environment', this.args.source.environmentId)?.name;
  }

  get actionLinkQuery() {
    // If the log is an attribute change, and the source is a webhook action,
    // link to the composer's "Response" tab directly
    if (
      !isEmpty(this.args.source.actionId) &&
      this.args.source.actionType === 'webhook_action' &&
      !isEmpty(this.args.source.campaignId) &&
      this.args.log?.type === ActivityLogType.AttributeChange
    ) {
      return {
        tab: ComposerSection.ResponseToProfileAttributesMappings,
      };
    }

    return null;
  }

  get actionInfo() {
    if (isEmpty(this.args.source.actionType)) {
      return null;
    }

    let actionType = this.args.source.actionType as string | ActionType;

    // For a while the backend had the wrong action type, e.g. Twilio
    // instead of twilio_action. We handle that here by underscoring
    // the string (if it's more than one word) and adding `_action` if necessary.
    // Also, the type for collection queries might be QueryCollection, rather
    // than CollectionQuery.

    if (actionType === 'QueryCollection') {
      actionType = 'collection_query_action';
    }

    actionType = underscore(actionType);

    if (!actionType.includes('_action')) {
      actionType = `${actionType}_action`;
    }

    return actionInfo(actionType as ActionType);
  }

  get hasAction() {
    return !isEmpty(this.args.source.actionType) || !isEmpty(this.args.source.actionName);
  }

  get canLinkJourney() {
    if (isEmpty(this.args.source.subjectId)) {
      return false;
    }
    // Webhook triggered but don't have a campaign
    if (this.isSourceWebhookTriggered && this.args.source.campaignId == null) {
      return false;
    }
    // Not webhook triggered and no customer to link current journey to
    if (!this.isSourceWebhookTriggered && this.args.source.customerId == null) {
      return false;
    }

    return true;
  }

  get journeyLinkParams() {
    if (this.args.source.subjectId == null) {
      throw new Error('`subjectId` is required to make a journey link. verify canLinkJourney guard');
    }

    if (this.isSourceWebhookTriggered) {
      if (this.args.source.campaignId == null) {
        throw new Error('`campaignId`is required to make a journey link. verify canLinkJourney guard');
      }
      return {
        route: 'campaigns.campaign.overview.subjects.subject',
        models: [this.args.source.campaignId, this.args.source.subjectId],
      };
    }

    if (this.args.source.customerId == null) {
      throw new Error('`customerId` is required to make a journey link. verify canLinkJourney guard');
    }

    return {
      route: 'person.subjects.subject',
      models: [this.args.source.customerId, this.args.source.subjectId],
    };
  }
}
